import React from "react";
import i18next from "i18next";
import {theme as antdTheme} from "antd";
import * as Setting from "../../Setting";
import {renderAvironForgetPage} from "./aviron/AvironForgetPage";
import {renderAvironMfaSetupPage} from "./aviron/AvironMfaSetupPage";
import {renderAvironSignedInAccount} from "./aviron/AvironSignedInAccount";

export const DefaultLoginTheme = "original";

const AvironAccent = "#ff4106";
const AvironAccentHover = "#c12e00";
const AvironInk = "#161616";
const AvironBorder = "#8a8a8a";

// a login theme only restyles the sign-in, forgot-password and MFA setup pages: the page logic, signin items,
// signin methods and providers stay server-driven; "original" renders the pages exactly as upstream does
export const LoginThemes = [
  {name: "original", label: () => i18next.t("application:Original")},
  {name: "aviron-internal", label: () => "Aviron Internal", className: "aviron-login", getAntdTheme: getAvironAntdTheme, getStyle: getAvironStyle, renderAfterItem: renderAvironAfterItem, renderSignedInAccount: renderAvironSignedInAccount, renderForgetPage: renderAvironForgetPage, renderMfaSetupPage: renderAvironMfaSetupPage},
];

// the "Welcome back" heading, and the already signed-in account if any, sit under the logo
function renderAvironAfterItem(signinItemName, page) {
  if (signinItemName !== "Logo") {
    return null;
  }
  return (
    <React.Fragment>
      <div className="aviron-heading">
        <h1 className="aviron-title">{i18next.t("login:Welcome back")}</h1>
        <p className="aviron-subtitle">{i18next.t("login:Sign in to your Aviron account")}</p>
      </div>
      {page.showSignedInAccount ? renderAvironSignedInAccount(page) : null}
    </React.Fragment>
  );
}

export function getLoginThemeOptions() {
  return LoginThemes.map(theme => ({value: theme.name, label: theme.label()}));
}

// ?loginTheme=original|aviron-internal overrides the application's theme for one page view, for QA and rollback checks
export function getLoginThemeName(application) {
  const override = new URLSearchParams(window.location.search).get("loginTheme");
  const name = override ?? application?.loginTheme;
  return LoginThemes.some(theme => theme.name === name) ? name : DefaultLoginTheme;
}

export function getLoginTheme(application) {
  const name = getLoginThemeName(application);
  return LoginThemes.find(theme => theme.name === name);
}

// the theme paints its own page background, so it carries the application's background image
// itself, darkened so the white card stands out; without an image the page is plain ink
function getAvironStyle(application) {
  if (Setting.inIframe()) {
    return null;
  }
  const backgroundUrl = Setting.isMobile() ? application?.formBackgroundUrlMobile : application?.formBackgroundUrl;
  if (!backgroundUrl) {
    return null;
  }
  return {backgroundImage: `linear-gradient(rgba(22, 22, 22, 0.72), rgba(22, 22, 22, 0.72)), url(${backgroundUrl})`};
}

// the application's own theme (Theme > Customize) wins over the Aviron defaults
export function getAvironAntdTheme(application) {
  const themeData = application?.themeData?.isEnabled ? application.themeData : null;
  const colorPrimary = themeData?.colorPrimary || AvironAccent;
  const borderRadius = themeData?.borderRadius ?? 4;
  const isAvironAccent = colorPrimary.toLowerCase() === AvironAccent;

  return {
    // the Aviron card is always light, also for visitors on the dark algorithm
    algorithm: antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: colorPrimary,
      colorLink: AvironInk,
      colorLinkHover: AvironAccentHover,
      colorText: AvironInk,
      colorBorder: AvironBorder,
      colorError: "#b42318",
      borderRadius: borderRadius,
      controlHeight: 48,
      fontSize: 16,
      fontFamily: "'Work Sans', Arial, sans-serif",
    },
    components: {
      Button: {
        ...(isAvironAccent ? {colorPrimaryHover: AvironAccentHover, colorPrimaryActive: AvironAccentHover} : {}),
        defaultBorderColor: AvironInk,
        defaultColor: AvironInk,
        defaultHoverBg: AvironInk,
        defaultHoverColor: "#ffffff",
        defaultHoverBorderColor: AvironInk,
        fontWeight: 400,
      },
      Input: {
        activeBorderColor: AvironInk,
        hoverBorderColor: AvironInk,
        activeShadow: "0 0 0 3px rgba(255, 65, 6, 0.35)",
        paddingInline: 16,
      },
      Tabs: {
        inkBarColor: colorPrimary,
        itemColor: "#4f4f4f",
        itemSelectedColor: AvironInk,
        itemHoverColor: AvironInk,
      },
      Checkbox: {
        colorPrimary: AvironInk,
        colorPrimaryHover: AvironInk,
        // antd derives it from controlHeight (48 here), which makes a 24px box
        controlInteractiveSize: 16,
        fontSize: 14,
      },
    },
  };
}
